import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Translation from '@wayfair/translation';
import VisuallyHidden from '../VisuallyHidden';
import {deprecatedProp, generateTestIds} from '@homebase/common';
import StarFilledIcon from '@homebase/icons/dist/generated/StarFilledIcon';
import StarUnfilledIcon from '@homebase/icons/dist/generated/StarUnfilledIcon';
import './ReviewStarsInput.$BRAND.css.js';

const noop = () => {};

// This array is meant to be used as a set of keys for rendering the
// 5 review stars within a loop, defined here to avoid re-defining it
// on every render
const STAR_IDENTIFIERS = [1, 2, 3, 4, 5];

class ReviewStarsInput extends Component {
  static propTypes = {
    // The root id for the inputs, each star will have a derived id off of this value
    id: PropTypes.string.isRequired,
    // A callback function for when the rating value is changed
    onRatingChange: PropTypes.func,
    // A callback function called with the hovered star index or null
    onStarHover: PropTypes.func,
    // Text to show on the right (unless showRatingText is true and a star is hovered)
    hintText: PropTypes.node,
    // If true, the rating out of 5 stars is shown
    showRatingText: PropTypes.bool,
    // If true, interaction is prevented with the review stars (they will act simply as a visual indicator)
    isPastReview: PropTypes.bool,
    // The previous selected value of the input, it is used only if `isPastReview` is true
    pastReviewSelectedStar: PropTypes.number,
    // If true, indicates if the input is being edited
    isEditing: PropTypes.bool,
    // The overall rating value for the input
    rating: PropTypes.number,
    // If true, the labels are shown inline instead of below the stars
    inlineText: PropTypes.bool,
    // This is the label that is shown for each review star
    starCopy: PropTypes.objectOf(PropTypes.node),
    // The root enzyme id used for the review stars
    enzymeId: deprecatedProp('Please use the testIds prop instead', [null]),
    // Custom text of hidden `<legend>` for a11y, please pass a Translation component
    legendText: PropTypes.node,
    // Enzyme and Codeception testing identifiers. Outputs data-enzyme-id and/or data-codeception-id attributes when set.
    testIds: PropTypes.shape({
      enzyme: PropTypes.string,
      codeception: PropTypes.string,
    }),
  };

  static defaultProps = {
    onRatingChange: noop,
    onStarHover: noop,
    hintText: null,
    id: null,
    showRatingText: false,
    inlineText: false,
    starCopy: null,
    isPastReview: false,
    pastReviewSelectedStar: null,
    isEditing: false,
    rating: null,
    enzymeId: null,
    legendText: null,
    testIds: {},
  };

  state = {
    hoveredStar: null,
  };

  shouldBeInteractive = () =>
    !this.props.isPastReview ||
    (this.props.isPastReview && this.props.isEditing);

  handleOnChangeStar = (rating) => {
    if (!this.shouldBeInteractive()) {
      return;
    }
    this.props.onRatingChange(rating);
  };

  setHoveredStarValue = (starHoverValue) => {
    /* If this is a past review and we're not yet editing it, don't update the hover star */
    if (!this.shouldBeInteractive()) {
      return;
    }
    this.props.onStarHover(starHoverValue);
    this.setState({hoveredStar: starHoverValue});
  };

  getRatingText = (starValue) => {
    if (starValue === null) {
      return null;
    }
    let starCopy = this.props.starCopy;
    if (this.props.starCopy == null) {
      starCopy = {
        1: <Translation msgid="reviewStarsInput.missedTheMark" />,
        2: <Translation msgid="reviewStarsInput.roomToImprove" />,
        3: <Translation msgid="reviewStarsInput.itsOk" />,
        4: <Translation msgid="reviewStarsInput.worksForMe" />,
        5: <Translation msgid="reviewStarsInput.justWhatIneed" />,
      };
    }
    return starCopy[starValue];
  };

  // If this is a past review, we want to pre-select the star so it displays what the user already submitted
  getDisplayRating = () =>
    this.props.isPastReview && !this.props.isEditing
      ? this.props.pastReviewSelectedStar
      : this.props.rating;

  render() {
    const {
      inlineText,
      id,
      enzymeId,
      testIds,
      showRatingText,
      hintText,
    } = this.props;

    let legendText;

    if (legendText == null) {
      legendText = <Translation msgid="reviewStarsInput.savedRating" />;
    }

    return (
      <div
        className={cx('ReviewStarsInput-wrapper', {
          'ReviewStarsInput-wrapper--inlineText': inlineText,
        })}
        data-hb-id="ReviewStarsInput"
      >
        <fieldset
          className={cx('ReviewStarsInput-stars', {
            'ReviewStarsInput-stars--inlineText': inlineText,
          })}
        >
          {legendText && (
            <legend>
              <VisuallyHidden>{legendText}</VisuallyHidden>
            </legend>
          )}
          {STAR_IDENTIFIERS.map((i) => (
            <label
              key={i}
              className="ReviewStarsInput-stars-label"
              onMouseOver={() => {
                this.setHoveredStarValue(i);
              }}
              onMouseOut={() => {
                this.setHoveredStarValue(null);
              }}
              onFocus={() => this.setHoveredStarValue(i)}
              onBlur={() => this.setHoveredStarValue(null)}
            >
              <input
                className="ReviewStarsInput-stars-input"
                type="radio"
                id={`${id}-star${i}`}
                name={`${id}-rating`}
                value={i}
                aria-label={Translation({
                  msgid: 'reviewStarsInput.inputLabel',
                  params: {rating: i},
                })}
                onChange={() => this.handleOnChangeStar(i)}
                data-enzyme-id={enzymeId ? `${enzymeId}-star-${i}` : null}
                {...generateTestIds({testIds, postfix: `star-${i}`})}
              />
              {this.getDisplayRating() >= i || this.state.hoveredStar >= i ? (
                <StarFilledIcon className="ReviewStarsInput-stars-icon" />
              ) : (
                <StarUnfilledIcon className="ReviewStarsInput-stars-icon" />
              )}
            </label>
          ))}
        </fieldset>
        {showRatingText && (
          <span
            role="status"
            aria-live="polite"
            className={cx('ReviewStarsInput-ratingText', {
              'ReviewStarsInput-ratingText--inlineText': inlineText,
            })}
          >
            {this.getRatingText(this.state.hoveredStar) || hintText}
          </span>
        )}
      </div>
    );
  }
}
export default ReviewStarsInput;
